import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { JOB_TYPE_OPTIONS } from '../constants/jobTypes';

const CATEGORY_OPTIONS = [
  { value: 'legal', label: '法律合规' },
  { value: 'finance', label: '财务审计' },
  { value: 'business', label: '商务拓展' },
  { value: 'engineering', label: '工程技术' },
  { value: 'hr', label: '人力资源' },
  { value: 'supply-chain', label: '供应链' },
  { value: 'other', label: '其他' },
];

function AdminPending() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [runs, setRuns] = useState([]);
  const [showRuns, setShowRuns] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') navigate('/');
  }, [user, navigate]);

  const loadPending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/jobs/admin/pending');
      setPending(res.data.jobs || []);
    } catch (err) {
      setMessage(err.response?.data?.error || '加载失败');
    }
    setLoading(false);
  }, []);

  const loadRuns = useCallback(async () => {
    try {
      const res = await api.get('/jobs/admin/scrape-runs');
      setRuns(res.data.runs || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    loadPending();
    loadRuns();
  }, [loadPending, loadRuns]);

  const triggerScrape = async () => {
    if (!window.confirm('立即触发一次抓取？预计需要 30-90 秒。')) return;
    setScraping(true);
    setMessage('抓取已启动，正在后台执行...');
    try {
      await api.post('/jobs/admin/scrape-now');
      // 5 秒后开始轮询
      setTimeout(() => {
        loadPending();
        loadRuns();
        setScraping(false);
        setMessage('抓取已开始。请稍等，几十秒后再点"刷新列表"。');
      }, 5000);
    } catch (err) {
      setMessage(err.response?.data?.error || '触发失败');
      setScraping(false);
    }
  };

  const approve = async (id) => {
    try {
      await api.put(`/jobs/admin/pending/${id}/approve`);
      setPending(p => p.filter(j => j.id !== id));
      setMessage('✅ 已上线');
    } catch (err) {
      setMessage(err.response?.data?.error || '上线失败');
    }
  };

  const reject = async (id) => {
    if (!window.confirm('确定拒绝并删除？')) return;
    try {
      await api.delete(`/jobs/admin/pending/${id}`);
      setPending(p => p.filter(j => j.id !== id));
      setMessage('已拒绝');
    } catch (err) {
      setMessage(err.response?.data?.error || '拒绝失败');
    }
  };

  const startEdit = (job) => {
    setEditingId(job.id);
    setEditDraft({ ...job });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/jobs/admin/pending/${editingId}`, editDraft);
      setPending(p => p.map(j => j.id === editingId ? { ...j, ...editDraft } : j));
      setEditingId(null);
      setEditDraft({});
      setMessage('已保存');
    } catch (err) {
      setMessage(err.response?.data?.error || '保存失败');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({});
  };

  const toggleSelect = (id) => {
    setSelectedIds(s => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === pending.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(pending.map(j => j.id)));
  };

  const batchAction = async (action) => {
    if (selectedIds.size === 0) { setMessage('请先选择岗位'); return; }
    const verb = action === 'approve' ? '批准上线' : '拒绝并删除';
    if (!window.confirm(`确定${verb}选中的 ${selectedIds.size} 个岗位？`)) return;
    try {
      const ids = Array.from(selectedIds);
      const res = await api.post('/jobs/admin/pending/batch', { action, ids });
      setPending(p => p.filter(j => !selectedIds.has(j.id)));
      setSelectedIds(new Set());
      setMessage(res.data.message);
    } catch (err) {
      setMessage(err.response?.data?.error || '批量操作失败');
    }
  };

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="container" style={{ maxWidth: 1100, padding: '20px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <h1 style={{ fontSize: '1.5rem', margin: 0 }}>📥 抓取审核队列</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link to="/admin" style={{ fontSize: 14, color: 'var(--text-light)', textDecoration: 'none', alignSelf: 'center' }}>← 返回管理后台</Link>
          <button onClick={loadPending} disabled={loading}
            style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid var(--border)', background: '#fff', cursor: 'pointer' }}>
            🔄 刷新列表
          </button>
          <button onClick={triggerScrape} disabled={scraping}
            style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--primary)', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
            {scraping ? '抓取中...' : '🚀 立即抓取'}
          </button>
        </div>
      </div>

      {message && (
        <div style={{ background: '#f0f7ff', border: '1px solid #cfe2ff', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 14 }}>
          {message}
        </div>
      )}

      {/* 批量操作栏 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, padding: '8px 12px', background: '#f8f9fa', borderRadius: 6, fontSize: 14 }}>
        <input type="checkbox"
               checked={pending.length > 0 && selectedIds.size === pending.length}
               onChange={selectAll} />
        <span style={{ color: 'var(--text-light)' }}>
          共 {pending.length} 条，已选 {selectedIds.size} 条
        </span>
        {selectedIds.size > 0 && (
          <>
            <button onClick={() => batchAction('approve')}
              style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #28a745', background: '#28a745', color: '#fff', cursor: 'pointer', fontSize: 13 }}>
              ✅ 批量批准
            </button>
            <button onClick={() => batchAction('reject')}
              style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #dc3545', background: '#dc3545', color: '#fff', cursor: 'pointer', fontSize: 13 }}>
              ❌ 批量拒绝
            </button>
          </>
        )}
        <span style={{ flex: 1 }} />
        <button onClick={() => setShowRuns(v => !v)}
          style={{ padding: '4px 10px', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-light)', fontSize: 13 }}>
          {showRuns ? '隐藏' : '查看'}抓取历史
        </button>
      </div>

      {showRuns && (
        <div style={{ marginBottom: 16, border: '1px solid var(--border)', borderRadius: 6, padding: 10, fontSize: 13, background: '#fafbfc' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>最近抓取记录</div>
          {runs.length === 0 ? <div style={{ color: 'var(--text-light)' }}>暂无</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {runs.slice(0, 10).map(r => <RunRow key={r.id} run={r} />)}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-light)' }}>加载中...</div>
      ) : pending.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-light)', background: '#fafbfc', borderRadius: 8 }}>
          🎉 待审核队列为空
          <div style={{ fontSize: 13, marginTop: 8 }}>点上面的"立即抓取"按钮触发一次新抓取</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {pending.map(job => (
            <div key={job.id}
              style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14, background: '#fff' }}>

              {editingId === job.id ? (
                /* 编辑模式 */
                <EditForm draft={editDraft} setDraft={setEditDraft} onSave={saveEdit} onCancel={cancelEdit} />
              ) : (
                /* 展示模式 */
                <div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
                    <input type="checkbox"
                      checked={selectedIds.has(job.id)}
                      onChange={() => toggleSelect(job.id)}
                      style={{ marginTop: 4 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                        <h3 style={{ margin: 0, fontSize: '1.1rem', flex: '1 1 auto' }}>{job.title || '(无标题)'}</h3>
                        <span style={{ fontSize: 12, padding: '2px 8px', background: '#f1f3f5', borderRadius: 10, color: 'var(--text-light)' }}>
                          {job.source || 'unknown'}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--text-light)', marginTop: 4 }}>
                        {job.company || '(未知公司)'}
                        {' · '}
                        {job.location || '(无地点)'}
                        {' · '}
                        {jobTypeLabel(job.jobType)}
                        {' · '}
                        {categoryLabel(job.category)}
                        {job.salary ? ` · ${job.salary}` : ''}
                      </div>
                    </div>
                  </div>

                  {job.description && (
                    <div style={{ fontSize: 13, color: '#444', marginTop: 8, padding: 8, background: '#fafbfc', borderRadius: 4, maxHeight: 120, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                      {job.description.substring(0, 400)}{job.description.length > 400 ? '...' : ''}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    <button onClick={() => approve(job.id)}
                      style={{ padding: '6px 14px', borderRadius: 4, border: 'none', background: '#28a745', color: '#fff', cursor: 'pointer', fontSize: 13 }}>
                      ✅ 批准上线
                    </button>
                    <button onClick={() => startEdit(job)}
                      style={{ padding: '6px 14px', borderRadius: 4, border: '1px solid var(--border)', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
                      ✏️ 编辑
                    </button>
                    <button onClick={() => reject(job.id)}
                      style={{ padding: '6px 14px', borderRadius: 4, border: '1px solid #dc3545', background: '#fff', color: '#dc3545', cursor: 'pointer', fontSize: 13 }}>
                      ❌ 拒绝
                    </button>
                    {job.sourceUrl && (
                      <a href={job.sourceUrl} target="_blank" rel="noopener noreferrer"
                        style={{ padding: '6px 14px', borderRadius: 4, border: '1px solid var(--border)', background: '#fff', color: 'var(--text-light)', textDecoration: 'none', fontSize: 13 }}>
                        🔗 查看来源
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RunRow({ run }) {
  const [expanded, setExpanded] = useState(false);
  const sr = run.scraperResults || {};
  const summary = Object.entries(sr).map(([n, v]) =>
    `${n}: 抓到 ${v.found ?? 0} / 新增 ${v.new ?? 0}${v.duplicate ? ` / 重复 ${v.duplicate}` : ''}${v.errors ? ` / ❌${v.errors}` : ''}`
  ).join(' | ');

  return (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 4, padding: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, cursor: 'pointer' }} onClick={() => setExpanded(v => !v)}>
        <span style={{ fontFamily: 'monospace', color: 'var(--text-light)' }}>
          {new Date(run.startedAt).toLocaleString('zh-CN')}
        </span>
        <span style={{
          padding: '1px 6px', borderRadius: 4, fontSize: 11,
          background: run.status === 'done' ? '#d4edda' : run.status === 'failed' ? '#f8d7da' : '#fff3cd',
          color: run.status === 'done' ? '#155724' : run.status === 'failed' ? '#721c24' : '#856404',
        }}>{run.status}</span>
        <span style={{ flex: 1, color: '#333' }}>
          总抓到 {run.totalFound ?? 0} · 新增 {run.totalNew ?? 0}
          {run.errorMsg && <span style={{ color: '#dc3545', marginLeft: 8 }}>· ❌ {run.errorMsg.substring(0, 80)}</span>}
        </span>
        <span style={{ color: 'var(--text-light)' }}>{expanded ? '▲ 收起' : '▼ 展开'}</span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 4 }}>{summary || '(无详情)'}</div>
      {expanded && (
        <div style={{ marginTop: 8, padding: 8, background: '#fafbfc', borderRadius: 3, fontSize: 11, fontFamily: 'monospace' }}>
          {Object.entries(sr).map(([n, v]) => (
            <details key={n} style={{ marginBottom: 6 }}>
              <summary style={{ fontWeight: 600, cursor: 'pointer' }}>
                {n}: {v.success ? '✅' : '❌'} {v.enabled === false ? '(禁用)' : ''} 找到 {v.found ?? 0}, 新增 {v.new ?? 0}
              </summary>
              {v.message && <div style={{ marginTop: 4, color: '#666' }}>{v.message}</div>}
              {v.topLevelError && <div style={{ marginTop: 4, color: '#dc3545' }}>错误：{v.topLevelError}</div>}
              {v.debug && (
                <pre style={{ marginTop: 4, overflow: 'auto', maxHeight: 240, fontSize: 10, whiteSpace: 'pre-wrap', color: '#333' }}>
                  {JSON.stringify(v.debug, null, 2)}
                </pre>
              )}
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

function EditForm({ draft, setDraft, onSave, onCancel }) {
  const upd = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const inputStyle = { width: '100%', padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 13 };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
      <Field label="标题"><input style={inputStyle} value={draft.title || ''} onChange={e => upd('title', e.target.value)} /></Field>
      <Field label="公司"><input style={inputStyle} value={draft.company || ''} onChange={e => upd('company', e.target.value)} /></Field>
      <Field label="地点"><input style={inputStyle} value={draft.location || ''} onChange={e => upd('location', e.target.value)} /></Field>
      <Field label="薪资"><input style={inputStyle} value={draft.salary || ''} onChange={e => upd('salary', e.target.value)} /></Field>
      <Field label="工作类型">
        <select style={inputStyle} value={draft.jobType || 'overseas-market'} onChange={e => upd('jobType', e.target.value)}>
          {JOB_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="类别">
        <select style={inputStyle} value={draft.category || 'other'} onChange={e => upd('category', e.target.value)}>
          {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="联系方式" full>
        <input style={inputStyle} value={draft.contactInfo || ''} onChange={e => upd('contactInfo', e.target.value)} />
      </Field>
      <Field label="描述" full>
        <textarea style={{ ...inputStyle, minHeight: 80, fontFamily: 'inherit' }} value={draft.description || ''} onChange={e => upd('description', e.target.value)} />
      </Field>
      <Field label="要求" full>
        <textarea style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }} value={draft.requirements || ''} onChange={e => upd('requirements', e.target.value)} />
      </Field>
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, marginTop: 6 }}>
        <button onClick={onSave}
          style={{ padding: '6px 14px', borderRadius: 4, border: 'none', background: 'var(--primary)', color: '#fff', cursor: 'pointer', fontSize: 13 }}>
          💾 保存
        </button>
        <button onClick={onCancel}
          style={{ padding: '6px 14px', borderRadius: 4, border: '1px solid var(--border)', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
          取消
        </button>
      </div>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto' }}>
      <label style={{ fontSize: 12, color: 'var(--text-light)', display: 'block', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

function jobTypeLabel(v) {
  const map = {
    'overseas-market': '国内负责海外市场',
    'global-market': '国内负责全球市场',
    'foreign-domestic': '国内外资企业',
    'expat': '外派',
    'local-hire': '海外本地招聘',
  };
  return map[v] || v || '(未分类)';
}

function categoryLabel(v) {
  const map = { legal: '法律', finance: '财务', business: '商务', engineering: '工程', hr: '人力', 'supply-chain': '供应链', other: '其他' };
  return map[v] || v || '(未分类)';
}

export default AdminPending;
